import _ from "lodash";
import React, { useState } from "react";
import { useI18n } from "Hooks";
import classNames from "classnames";
import { Avatar, Tabs, Badge, IconButton } from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import classes from "./UserParticipantList.scss";
import { ENTITY_TYPE } from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

const ListItem = ({ id, label, subText, handleRemoveEntity }) => {
  const handleItemClick = e => {
    e.stopPropagation();
    handleRemoveEntity(id);
  };
  return (
    <div className={classes.flexItem}>
      <div className={classes.itemContainer}>
        <Avatar dsVersion="2.0" id={id} name={label} shape="square" />
        <div className={classes.itemDetail}>
          <div className={classNames("text-body", classes.itemLabel)}>
            {label}
          </div>
          <div className={classNames("text-body-s", classes.itemCountRow)}>
            {subText}
          </div>
        </div>
      </div>
      <IconButton
        dsVersion="2.0"
        size="x-small"
        variant="neutral"
        type="plain"
        icon={<DeleteOutlined />}
        onClick={handleItemClick}
      />
    </div>
  );
};

const getBadge = value => {
  return (
    <Badge dsVersion="2.0" variant="subtle" value={value} size={"xx-small"} />
  );
};

const UserParticipantList = ({ participantSelections, handleRemoveEntity }) => {
  const { t } = useI18n(["common"]);

  const formattedCustomParticipants = _.groupBy(
    participantSelections[ENTITY_TYPE.CUSTOM],
    "__typename"
  );
  const userByUserType = {
    [ENTITY_TYPE.STAFF]: formattedCustomParticipants["Staff"] || [],
    [ENTITY_TYPE.STUDENT]: formattedCustomParticipants["Student"] || [],
    [ENTITY_TYPE.PARENT]: formattedCustomParticipants["FamilyMember"] || [],
  };

  const segmentOptions = [
    {
      value: "STAFF",
      label: t("common:staff_member_other"),
      suffix: getBadge(_.size(userByUserType?.STAFF)),
    },
    {
      value: "STUDENT",
      label: t("common:student_other"),
      suffix: getBadge(_.size(userByUserType?.STUDENT)),
    },
    {
      value: "PARENT",
      label: t("common:family_member_other"),
      suffix: getBadge(_.size(userByUserType?.PARENT)),
    },
  ];

  const filteredSegmentOptions = _.filter(segmentOptions, option => {
    return _.size(userByUserType[option?.value]);
  });

  const [selectedUserType, setSelectedUserType] = useState(
    _.first(filteredSegmentOptions)?.value
  );

  const handleSegmentChange = value => {
    setSelectedUserType(value);
  };

  // On delete entity there can be a case where the selected user type is not available in the filtered options.
  // In that case, we reset the selected user type to the first available option.
  // this is to ensure that if the selected user type is not available in the filtered options,
  // it defaults to the first available option
  if (
    !_.includes(_.map(filteredSegmentOptions, "value"), selectedUserType) &&
    _.size(filteredSegmentOptions)
  ) {
    setSelectedUserType(_.first(filteredSegmentOptions)?.value);
  }

  return (
    <div className={classes.userParticipantList}>
      <div className={classes.tabsContainer}>
        <Tabs
          dsVersion={"2.0"}
          options={filteredSegmentOptions}
          value={selectedUserType}
          onChange={handleSegmentChange}
          type={"fixed"}
          variant={"inline"}
          containerStyle={{
            marginBottom: "8px",
          }}
        />
      </div>

      <div className={classes.itemList}>
        {_.map(userByUserType[selectedUserType], user => {
          const userType = _.lowerCase(selectedUserType);
          const formattedName = getFormattedUserName({ user, type: userType });
          return (
            <ListItem
              key={user.id}
              id={user.id}
              label={formattedName}
              handleRemoveEntity={handleRemoveEntity}
            />
          );
        })}
      </div>
    </div>
  );
};

export default UserParticipantList;
